import {Component, Input, OnInit} from '@angular/core';
import {
  emptyOverride,
  Group,
  GroupOverride,
  OverrideInstruction,
  OverridePosition,
  OverridePositionRequest,
  PositionTypeEnum,
  Product,
  ProductOverride,
  RelatedProductsInfo
} from "app/models/menu.model";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {
  EditRelatedProductsData,
  EditRelatedProductsModalComponent
} from "app/pages/menu/shared/edit-related-products-modal/edit-related-products-modal.component";
import {Organization} from "app/models/organization.model";
import {MenuOverrideService} from "app/services/menu-override.service";
import {MenuService} from "app/services/menu.service";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
  selector: 'app-related-products',
  templateUrl: './related-products.component.html',
  styleUrls: ['./related-products.component.scss']
})
export class RelatedProductsComponent implements OnInit {
  @Input() allowOverrides: boolean;
  @Input() relatedProductInfos?: RelatedProductsInfo[];
  @Input() organization: Organization;
  @Input() subject: Product | Group;
  @Input() originalSubject: Product | Group;
  @Input() overridePosition: OverridePosition;
  @Input() menuSectionId: string;
  @Input() selectedMenuSections: any;

  products: Map<string, Product> = new Map<string, Product>();

  initValues: RelatedProductsInfo[];

  positionType: PositionTypeEnum = PositionTypeEnum.Product;

  constructor(
    public dialog: MatDialog,
    private translate: TranslateService,
    private store: Store,
    private menuService: MenuService,
  ) {
  }

  ngOnInit(): void {
    this.store.select(MenuSelectors.selectDictionaryProducts)
      .pipe(untilDestroyed(this))
      .subscribe(products => {
        this.products = products;
      });

    this.positionType = "groupStructure" in this.subject ? PositionTypeEnum.Group : PositionTypeEnum.Product;
    this.initValues = [];
    if(!this.relatedProductInfos){
      this.relatedProductInfos = [];
    }
    for (const relatedProductInfo of this.relatedProductInfos) {
      this.initValues.push({
        name: relatedProductInfo.name,
        nameTranslations: relatedProductInfo.nameTranslations,
        products: [...relatedProductInfo.products],
        legacy: relatedProductInfo.legacy,
      });
    }
  }

  onOpen(rpi?: RelatedProductsInfo): void {
    const param = rpi ?? {} as RelatedProductsInfo;
    this.dialog.open(EditRelatedProductsModalComponent,{
      data: {
        relatedProductInfo: param,
        organization: this.organization,
        product: this.subject,
        products: this.products,
        relatedProductInfos: this.relatedProductInfos,
        isNew: !rpi,
      } as EditRelatedProductsData
    },).afterClosed().subscribe(async result => {
      if(result.created) {
        // if isNew == true
        this.relatedProductInfos.push(result.created);
      } else if(result.updated) {
        this.relatedProductInfos[this.relatedProductInfos.indexOf(rpi)] = result.updated;
      } else if (result.deleted){
        this.relatedProductInfos = [...this.relatedProductInfos.filter(item => item.name != rpi.name)];
      }
      await this.saveRelatedProducts();
    });
  }

  async saveRelatedProducts() {
    // const positionType = this.product is Product ? PositionTypeEnum.Product : PositionTypeEnum.Group;
    let override: ProductOverride | GroupOverride;
    let isOverrideExists: boolean;
    if (!this.overridePosition) {
      this.overridePosition = {
        positionType: this.positionType,
        positionId: this.subject.externalId,
        override: emptyOverride,
        menuSectionId: this.menuSectionId,
      };
      override = {};
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      override = this.positionType == PositionTypeEnum.Product
        ? JSON.parse(this.overridePosition.override) as ProductOverride
        : JSON.parse(this.overridePosition.override) as GroupOverride;
    }
    const overrideInstructions: OverrideInstruction[] = [];

    if (JSON.stringify(this.initValues.sort()) !== JSON.stringify(this.relatedProductInfos.sort())){
      MenuOverrideService.overrideArrayFiled(this.subject, this.originalSubject, 'relatedProductsInfo', this.relatedProductInfos, isOverrideExists, overrideInstructions);
    }

    if(isOverrideExists){
      if(override.relatedProductsName){
        MenuOverrideService.overrideTextFiled(this.subject, this.originalSubject, 'relatedProductsName', null, isOverrideExists, overrideInstructions, true);
      }
      if(override.relatedProductsNameTranslations != null){
        for (const lang of this.organization.additionalLanguages){
          MenuOverrideService.overrideTranslationFiled(
            this.subject,
            this.originalSubject,
            'relatedProductsNameTranslations',
            lang,
            null,
            isOverrideExists,
            override,
            overrideInstructions);
        }
      }
    }
    if(overrideInstructions.length > 0){
      await this.saveOverridePosition(overrideInstructions, this.overridePosition);
    }
  }

  private async saveOverridePosition(overrideInstructions: OverrideInstruction[], productOverridePosition: OverridePosition) {
    const menuSectionId = this.menuSectionId;
    if (overrideInstructions.length > 0) {
      const savedMenuSections = this.getSavedMenuSections();

      const productOverridePositionRequest: OverridePositionRequest = {
        ...productOverridePosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(menuSectionId, [productOverridePositionRequest], savedMenuSections)
        .then(_=> _)
        .catch(error => alert(error.error));
    }
  }

  getSavedMenuSections(): string []  {
    return this.menuService.getSavedMenuSectionsIds(this.selectedMenuSections);
  }

  async onDelete(rpi: RelatedProductsInfo) {
    this.relatedProductInfos = [...this.relatedProductInfos.filter(item => item.name != rpi.name)];
    await this.saveRelatedProducts();
  }
}
